// A learner's results in the numbered practice sets of one bank: on the account when signed in
// (/api/practice/sets), in this browser for guests. Read after mount only, so the server render never depends on it.

import type { SetRecord } from '@/prakrit/components/practice/practice-types'

import { useCallback, useEffect, useState } from 'react'
import { useProgress } from '@/prakrit/lib/client/progress'

const STORAGE_KEY = 'vp-practice-sets'

type Store = Record<string, Record<string, SetRecord>>

const readStore = (): Store => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) as unknown : null

        return parsed && typeof parsed === 'object' ? parsed as Store : {}
    }
    catch {
        return {}
    }
}

const writeStore = (store: Store): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
    }
    catch {
        // Storage full or blocked: the result is only kept for this visit.
    }
}

const better = (old: SetRecord | undefined, correct: number, size: number): SetRecord => ({
    best: Math.max(old?.best ?? 0, correct),
    size,
    plays: (old?.plays ?? 0) + 1
})

export interface SetProgress {
    records: Record<string, SetRecord>
    loaded: boolean
    record: (set: number, correct: number, size: number) => void
}

export const useSetProgress = (key: string | null): SetProgress => {
    const { ready, signedIn } = useProgress()
    const [records, setRecords] = useState<Record<string, SetRecord>>({})
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (!key || !ready) return

        let cancelled = false

        setLoaded(false)

        if (!signedIn) {
            setRecords(readStore()[key] ?? {})
            setLoaded(true)

            return
        }

        fetch(`/api/practice/sets?key=${encodeURIComponent(key)}`, { cache: 'no-store' })
            .then(res => (res.ok ? res.json() as Promise<{ sets: Record<string, SetRecord> | null }> : { sets: null }))
            .then(data => {
                if (!cancelled) setRecords(data.sets ?? {})
            })
            .catch(() => {
                if (!cancelled) setRecords({})
            })
            .finally(() => {
                if (!cancelled) setLoaded(true)
            })

        return () => {
            cancelled = true
        }
    }, [key, ready, signedIn])

    const record = useCallback((set: number, correct: number, size: number) => {
        if (!key) return

        setRecords(old => ({ ...old, [String(set)]: better(old[String(set)], correct, size) }))

        if (!signedIn) {
            const store = readStore()
            const bank = store[key] ?? {}

            store[key] = { ...bank, [String(set)]: better(bank[String(set)], correct, size) }
            writeStore(store)

            return
        }

        void fetch('/api/practice/sets', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key, set, correct, size })
        }).catch(() => undefined)
    }, [key, signedIn])

    return { records, loaded, record }
}
